// Supported timezones in the backend
const SUPPORTED_TIMEZONES = [
  "America/Chicago",
  "America/Anchorage",
  "America/Phoenix",
  "America/Denver",
  "America/Los_Angeles",
  "America/New_York",
  "Pacific/Honolulu",
  "America/Toronto",
  "America/Winnipeg",
  "America/Edmonton",
  "America/Vancouver",
  "America/Halifax",
  "America/St_Johns",
  "America/Whitehorse",
  "America/Iqaluit",
];

// Timezone mapping to normalize browser-detected timezones to supported backend timezones
const TIMEZONE_ALIASES: Record<string, string> = {
  // US aliases
  "US/Central": "America/Chicago",
  "US/Alaska": "America/Anchorage",
  "US/Arizona": "America/Phoenix",
  "US/Mountain": "America/Denver",
  "US/Pacific": "America/Los_Angeles",
  "US/Eastern": "America/New_York",
  "US/Hawaii": "Pacific/Honolulu",

  // Other common aliases
  "America/Detroit": "America/New_York",
  "America/Indianapolis": "America/New_York",
  "America/Kentucky/Louisville": "America/Chicago",
  "America/Kentucky/Monticello": "America/New_York",
  "America/Nome": "America/Anchorage",
  "America/Juneau": "America/Anchorage",
  "America/Sitka": "America/Anchorage",
  "America/Yakutat": "America/Anchorage",
  "America/Boise": "America/Denver",
  "America/Shiprock": "America/Denver",
  "America/North_Dakota/Center": "America/Chicago",
  "America/North_Dakota/New_Salem": "America/Chicago",
  "America/North_Dakota/Beulah": "America/Chicago",
  "America/Menominee": "America/Chicago",
  "America/Metlakatla": "America/Anchorage",

  // Canada aliases
  "Canada/Eastern": "America/Toronto",
  "Canada/Central": "America/Winnipeg",
  "Canada/Mountain": "America/Edmonton",
  "Canada/Pacific": "America/Vancouver",
  "Canada/Atlantic": "America/Halifax",
  "Canada/Newfoundland": "America/St_Johns",
  "America/Montreal": "America/Toronto",
  "America/Nipigon": "America/Toronto",
  "America/Thunder_Bay": "America/Toronto",
  "America/Pangnirtung": "America/Iqaluit",
  "America/Atikokan": "America/Toronto",
  "America/Resolute": "America/Iqaluit",
  "America/Rankin_Inlet": "America/Winnipeg",
  "America/Cambridge_Bay": "America/Edmonton",
  "America/Regina": "America/Winnipeg",
  "America/Swift_Current": "America/Winnipeg",
  "America/Dawson_Creek": "America/Edmonton",
  "America/Fort_Nelson": "America/Edmonton",
  "America/Creston": "America/Denver",
  "America/Dawson": "America/Whitehorse",
  "America/Inuvik": "America/Edmonton",
  "America/Yellowknife": "America/Edmonton",
  "America/Blanc-Sablon": "America/Halifax",
  "America/Goose_Bay": "America/Halifax",
  "America/Glace_Bay": "America/Halifax",
  "America/Moncton": "America/Halifax",
};

/**
 * Normalizes a browser-detected timezone to a supported backend timezone.
 * If the timezone is already supported, returns it as-is.
 * If it has a known alias, returns the canonical supported timezone.
 * Otherwise, defaults to America/New_York.
 */
export function normalizeTimezone(browserTimezone: string): string {
  // If it's already a supported timezone, use it
  if (SUPPORTED_TIMEZONES.includes(browserTimezone)) {
    return browserTimezone;
  }

  // Check if there's a known alias mapping
  if (TIMEZONE_ALIASES[browserTimezone]) {
    return TIMEZONE_ALIASES[browserTimezone];
  }

  // Default to Eastern time if we can't map it
  return "America/New_York";
}

/**
 * Gets the browser's timezone and normalizes it to a supported backend timezone.
 */
export function getBrowserTimezone(): string {
  try {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return normalizeTimezone(browserTimezone);
  } catch (error) {
    // Fallback if Intl is not supported
    return "America/New_York";
  }
}
